import * as yup from "yup"

/**
 * Common validation rules that can be reused across different forms
 * These functions return Yup validation chains that can be composed into schemas
 */

/**
 * Creates a required field validation
 * @param errorMessage Error message to display
 */
export const createRequiredValidation = (errorMessage: string) => {
  return yup.string().required(errorMessage)
}

/**
 * Creates email validation
 * @param requiredErrorMessage Required field error message
 * @param invalidEmailMessage Invalid email format error message
 */
export const createEmailValidation = (
  requiredErrorMessage: string,
  invalidEmailMessage: string,
) => {
  return yup
    .string()
    .required(requiredErrorMessage)
    .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, invalidEmailMessage)
}

/**
 * Creates phone number validation (digits only)
 * @param requiredErrorMessage Required field error message
 * @param digitsOnlyMessage Digits only error message
 */
export const createPhoneNumberValidation = (
  requiredErrorMessage: string,
  digitsOnlyMessage: string,
) => {
  return yup
    .string()
    .required(requiredErrorMessage)
    .matches(/^[0-9]+$/, digitsOnlyMessage)
}

/**
 * Creates ID/number validation (digits only)
 * @param requiredErrorMessage Required field error message
 * @param digitsOnlyMessage Digits only error message
 */
export const createIdNumberValidation = (
  requiredErrorMessage: string,
  digitsOnlyMessage: string,
) => {
  return yup
    .string()
    .required(requiredErrorMessage)
    .matches(/^[0-9]+$/, digitsOnlyMessage)
}

/**
 * Creates terms agreement checkbox validation
 * @param errorMessage Error message to display
 */
export const createAgreeTermsValidation = (errorMessage: string) => {
  return yup.boolean().oneOf([true], errorMessage).required(errorMessage)
}

/**
 * Creates license plate validation
 * @param errorMessage Error message to display
 */
export const createLicensePlateValidation = (errorMessage: string) => {
  return yup.string().required(errorMessage)
}

/**
 * Creates extended phone number validation that checks for format and minimum length
 * @param requiredErrorMessage Required field error message
 * @param invalidFormatMessage Invalid format error message
 * @param minLength Minimum length of the phone number
 */
export const createExtendedPhoneValidation = (
  requiredErrorMessage: string,
  invalidFormatMessage: string,
  minLength: number = 9,
) => {
  return yup
    .string()
    .required(requiredErrorMessage)
    .min(minLength, invalidFormatMessage)
    .matches(/^[0-9-]+$/, invalidFormatMessage)
}

/**
 * Creates Israeli ID validation with checksum validation
 * @param requiredErrorMessage Required field error message
 * @param invalidFormatMessage Invalid format error message
 * @param isValidIsraeliID Function to check if ID passes checksum validation
 */
export const createIsraeliIdValidation = (
  requiredErrorMessage: string,
  invalidFormatMessage: string,
  isValidIsraeliID: (id: string) => boolean,
) => {
  return yup
    .string()
    .required(requiredErrorMessage)
    .matches(/^\d{9}$/, invalidFormatMessage)
    .test("israeli-id-checksum", invalidFormatMessage, (value) => {
      if (!value) return true
      return isValidIsraeliID(value)
    })
}

/**
 * Creates car number validation
 * @param requiredErrorMessage Required field error message
 * @param invalidFormatMessage Invalid format error message
 */
export const createCarNumberValidation = (
  requiredErrorMessage: string,
  invalidFormatMessage: string,
) => {
  return yup
    .string()
    .required(requiredErrorMessage)
    .matches(/^[0-9-]+$/, invalidFormatMessage)
}

/**
 * Creates optional text validation (not required)
 * @param invalidFormatMessage Invalid format error message (optional)
 */
export const createOptionalTextValidation = (invalidFormatMessage?: string) => {
  let schema = yup.string()
  if (invalidFormatMessage) {
    schema = schema.matches(
      /^[\u0590-\u05FF\u200F\u200E\s]*$/,
      invalidFormatMessage,
    )
  }
  return schema
}

/**
 * Creates optional numeric validation (not required)
 * @param invalidFormatMessage Invalid format error message (optional)
 */
export const createOptionalNumericValidation = (
  invalidFormatMessage?: string,
) => {
  let schema = yup.string()
  if (invalidFormatMessage) {
    schema = schema.matches(/^[0-9-]*$/, invalidFormatMessage)
  }
  return schema
}
